import React from "react";
import { useTheme } from "next-themes";
import { Quote, ArrowUpRight, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import SectionHeader from "@/components/brams/SectionHeader";
import Reveal from "@/components/brams/Reveal";

const ACCENT = "#E11D2E";

const testimonials = [
  {
    id: "lefa",
    brand: "LEFA ONE TRAINER",
    role: "Salle de sport privée",
    quote:
      "Brams Graphic a réalisé notre enseigne ainsi que l'ensemble de nos flyers publicitaires. Le résultat est moderne, impactant et parfaitement en accord avec notre image. Une collaboration sérieuse avec un véritable souci du détail.",
    logo: "https://customer-assets.emergentagent.com/job_brams-portfolio/artifacts/ftogbmte_Logo%20lefa%20one%20trainer%20copie.png",
  },
  {
    id: "cclean",
    brand: "C.CLEAN CANALISATION",
    role: "Débouchage & Assainissement",
    quote:
      "Nous avons confié à Brams Graphic la création de nos flyers, de nos cartes de visite ainsi que l'impression de tous nos supports de communication. Un rendu professionnel qui inspire confiance auprès de nos clients.",
    logo: "https://customer-assets.emergentagent.com/job_brams-portfolio/artifacts/7j29ee45_logo%20clean%20canalisation%20png.png",
  },
  {
    id: "antidote",
    brand: "ANTIDOTE PARIS",
    role: "Marque de Streetwear",
    quote:
      "Brams Graphic a imaginé et conçu l'identité visuelle complète de notre marque, avec un logo unique et un univers graphique fort. Une image de marque moderne, premium et immédiatement reconnaissable.",
    logo: "https://customer-assets.emergentagent.com/job_brams-portfolio/artifacts/uplow4zg_ddd.png",
  },
];

const ctaCard = {
  id: "cta",
  brand: "Votre prochain projet ?",
  role: "Peut-être le vôtre.",
  quote:
    "Chaque entreprise mérite une identité forte. Que ce soit pour un logo, un site internet, des supports imprimés ou un branding complet, Brams Graphic vous accompagne de la conception jusqu'à la réalisation.",
};

function ClientLogo({ src, alt, isDark }) {
  return (
    <div
      data-testid="testimonial-logo"
      className={cn(
        "inline-flex items-center justify-center px-4 py-2 rounded-xl",
        isDark
          ? "bg-white/[0.04] border border-white/[0.10]"
          : "bg-black/[0.05] border border-black/[0.10]"
      )}
    >
      <img
        src={src}
        alt={alt}
        className="h-9 md:h-10 w-auto max-w-[160px] object-contain select-none"
        draggable={false}
        loading="lazy"
        style={{
          mixBlendMode: isDark ? "screen" : "multiply",
          filter: isDark
            ? "grayscale(1) brightness(1.4) contrast(1.1)"
            : "grayscale(1) brightness(0.4) contrast(1.2)",
        }}
      />
    </div>
  );
}

export default function Testimonials() {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className={cn(
        "relative py-28 md:py-36",
        isDark ? "bg-black" : "bg-[#fafafa]"
      )}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <SectionHeader
          eyebrow="Témoignages"
          title={
            <>
              Ils nous font <em className="not-italic opacity-60">confiance</em>.
            </>
          }
          subtitle="Découvrez quelques entreprises et marques que nous avons accompagnées dans la création de leur identité visuelle, de leurs supports de communication et de leur présence digitale."
        />

        <div
          className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8"
          data-testid="testimonials-grid"
        >
          {testimonials.map((t, i) => (
            <Reveal key={t.id} delay={i * 100}>
              <ClientCard t={t} i={i} isDark={isDark} />
            </Reveal>
          ))}

          <Reveal delay={testimonials.length * 100}>
            <CtaCard t={ctaCard} i={testimonials.length} isDark={isDark} />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function ClientCard({ t, i, isDark }) {
  return (
    <article
      data-testid={`testimonial-card-${i}`}
      className={cn(
        "group relative p-8 md:p-10 rounded-2xl h-full transition-all duration-500 ease-out hover:-translate-y-1",
        isDark
          ? "bg-white/[0.025] border border-white/[0.08] soft-shadow-dark hover:bg-white/[0.04]"
          : "bg-white border border-black/[0.06] soft-shadow-light hover:border-black/[0.12]"
      )}
    >
      {/* Top accent line on hover */}
      <span
        className="absolute left-0 top-0 h-px w-0 group-hover:w-full transition-all duration-700 rounded-tl-2xl"
        style={{ background: ACCENT }}
      />

      <div className="flex items-center justify-between mb-8">
        <ClientLogo src={t.logo} alt={t.brand} isDark={isDark} />
        <Quote
          size={22}
          strokeWidth={1.2}
          className={cn(isDark ? "text-white/25" : "text-black/25")}
        />
      </div>

      <p
        className={cn(
          "font-display text-lg md:text-xl leading-relaxed tracking-tight",
          isDark ? "text-white/90" : "text-black/85"
        )}
      >
        &ldquo;{t.quote}&rdquo;
      </p>

      <div
        className={cn(
          "mt-8 pt-6 border-t flex items-center justify-between",
          isDark ? "border-white/10" : "border-black/10"
        )}
      >
        <div>
          <div className="font-display font-semibold text-sm tracking-tight">
            {t.brand}
          </div>
          <div
            className={cn(
              "text-[11px] uppercase tracking-[0.25em] mt-1",
              isDark ? "text-white/50" : "text-black/50"
            )}
          >
            {t.role}
          </div>
        </div>
        <div
          className={cn(
            "font-display font-semibold text-xs tracking-tighter opacity-40"
          )}
        >
          0{i + 1}
        </div>
      </div>
    </article>
  );
}

function CtaCard({ t, i, isDark }) {
  const scrollToContact = () => {
    const el = document.getElementById("contact");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <article
      data-testid={`testimonial-card-${i}`}
      onClick={scrollToContact}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          scrollToContact();
        }
      }}
      className={cn(
        "group relative p-8 md:p-10 rounded-2xl h-full overflow-hidden cursor-pointer transition-all duration-500 ease-out hover:-translate-y-1",
        isDark
          ? "bg-[#0A0A0A] border border-white/[0.10] hover:border-white/20"
          : "bg-black text-white border border-black hover:bg-black/95"
      )}
      style={{
        boxShadow: `0 20px 60px -30px ${ACCENT}66`,
      }}
    >
      {/* Red soft glow */}
      <div
        aria-hidden="true"
        className="absolute -inset-10 pointer-events-none"
        style={{
          background: `radial-gradient(closest-side, ${ACCENT}22, transparent 60%)`,
          filter: "blur(40px)",
        }}
      />
      {/* Top accent */}
      <span
        className="absolute left-0 top-0 h-[2px] w-full"
        style={{ background: ACCENT }}
      />

      <div className="relative flex items-center justify-between mb-8">
        <div
          className={cn(
            "inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[10px] uppercase tracking-[0.35em]",
            "bg-white/[0.06] border border-white/[0.15] text-white"
          )}
        >
          <Sparkles size={11} style={{ color: ACCENT }} />
          Le prochain c&apos;est vous
        </div>
        <ArrowUpRight
          size={22}
          strokeWidth={1.4}
          className="text-white/40 group-hover:text-white group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-500"
        />
      </div>

      <h3 className="relative font-display font-semibold text-2xl md:text-3xl tracking-tighter leading-tight text-white">
        {t.brand}
      </h3>
      <p
        className="relative mt-2 text-sm md:text-base font-display font-light"
        style={{ color: ACCENT }}
      >
        {t.role}
      </p>

      <p className="relative mt-6 font-body text-sm md:text-[15px] leading-relaxed text-white/75 max-w-xl">
        {t.quote}
      </p>

      <div
        className="relative mt-8 pt-6 border-t flex items-center justify-between"
        style={{ borderColor: "rgba(255,255,255,0.10)" }}
      >
        <div className="inline-flex items-center gap-3">
          <span
            className="inline-block w-1.5 h-1.5 rounded-full"
            style={{ background: ACCENT }}
          />
          <span className="text-[11px] uppercase tracking-[0.3em] text-white/75">
            Démarrer un projet
          </span>
        </div>
        <div className="font-display font-semibold text-xs tracking-tighter opacity-40 text-white">
          0{i + 1}
        </div>
      </div>
    </article>
  );
}
